/**
 * Graph Debug Panel — DEV/STAGING ADMIN-ONLY DIAGNOSTIC SURFACE.
 *
 * Per the current task: STOP adding new spouse/render fixes until we can
 * SEE exactly which layer is misclassifying a row. This panel surfaces
 * every signal that contributes to the visible placement decision for a
 * single selected person, so the operator can trace the failure end-to-end:
 *
 *     DB row → kinship engine → placement proof → pyramid layout →
 *     final card position → Needs Connection set
 *
 * VISIBILITY GATE (HARD — production hosts NEVER see this UI):
 *   The panel renders ONLY when BOTH of the following are true:
 *     • DEV_BYPASS_TEST_MODE is active (hostname includes "deploypad.app"),
 *       AND
 *     • the authenticated user is staff (owner / admin role) OR the URL
 *       contains "?debug=1" (still scoped under the deploypad-host check).
 *
 *   On kinscircleapp.com / www.kinscircleapp.com / any non-deploypad
 *   domain, `enabled` is forced to false and the component returns
 *   null — no debug UI, no STAFF chips, no diagnostic table, no repair
 *   button, no spacing placeholder.
 *
 * Pure diagnostic review. The operator may prepare structured repair
 * evidence, but this panel never mutates Tree, media, ancestry, spouse,
 * duplicate, or cached-layout state. Parent/child/cousin/niece-nephew
 * logic, billing, FamousPay, auth, onboarding, homepage, domain, pricing,
 * and the Resend pipeline are not touched.
 */


import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  GraphMember,
  computeKinship,
  kinshipLabel,
  useFamilyGraph,
} from '@/contexts/FamilyGraphContext';
import {
  resolveViewerAnchor,
  isPlacementProvable,
  computeUnplacedIds,
} from '@/lib/familyPlacement';
import { computePyramidLayout } from './pyramidLayout';
import {
  resolveCanonicalTreeBranchConsumer,
} from '@/governor/tree/CanonicalTreeBranchConsumerAuthority';
import {
  resolveCanonicalTreeLayoutPathRuntime,
} from '@/governor/tree/CanonicalTreeLayoutPathRuntimeSelector';
import {
  createTreeRuntimeBranchLayoutAuthoritySnapshot,
} from '@/governor/tree-runtime/TreeRuntimeBranchLayoutAuthoritySnapshot';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/ui/sonner';
import { DEV_BYPASS_TEST_MODE } from '@/lib/devBypass';
import {
  Bug,
  Search,
  RefreshCw,
  Wrench,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

// ─── Helpers ────────────────────────────────────────────────────────────
const norm = (s: string) =>
  (s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const same = (a: string | null | undefined, b: string | null | undefined) =>
  norm(a ?? '') === norm(b ?? '') && norm(a ?? '').length > 0;

const truthIcon = (v: boolean | null | undefined) => {
  if (v === true) return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 inline -mt-0.5 mr-1" />;
  if (v === false) return <XCircle className="w-3.5 h-3.5 text-rose-600 inline -mt-0.5 mr-1" />;
  return <AlertTriangle className="w-3.5 h-3.5 text-amber-500 inline -mt-0.5 mr-1" />;
};

interface DiagRow {
  label: string;
  value: React.ReactNode;
  ok?: boolean | null;
  hint?: string | null;
}

interface RepairResult {
  startedAt: string;
  finishedAt?: string;
  steps: Array<{ label: string; ok: boolean | null; detail?: string | null }>;
  finalSpouseLink: 'reciprocal' | 'one-sided' | 'missing' | 'unknown';
  duplicatesMerged: number;
  duplicateNamesKept: string;
  spouseAnchorId?: string | null;
  spouseAnchorName?: string | null;
}

// ─── Layout cache clearing ─────────────────────────────────────────────
// We don't ship a persisted pyramid-layout cache today, but legacy /
// in-progress branches may have set one. Wipe any KC-prefixed layout
// keys defensively. Pure best-effort. Wrapped in try/catch so a quota /
// privacy-mode failure can never block the repair flow.
const clearLayoutCacheKeys = (): string[] => {
  const cleared: string[] = [];
  try {
    if (typeof window === 'undefined') return cleared;
    const targets: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (!k) continue;
      if (
        k.startsWith('kc:layout') ||
        k.startsWith('kc:tree') ||
        k.startsWith('kc:pyramid') ||
        k.startsWith('kc:graph-snapshot')
      ) {
        targets.push(k);
      }
    }
    for (const k of targets) {
      try {
        window.localStorage.removeItem(k);
        cleared.push(k);
      } catch {
        /* ignore single-key failure */
      }
    }
  } catch {
    /* ignore */
  }
  return cleared;
};

// ─── Component ─────────────────────────────────────────────────────────
const GraphDebugPanel: React.FC = () => {
  const auth = useAuth();
  const isStaff = !!auth?.isStaff;
  const {
    members,
    memories,
    photos,
    comments,
    viewerMemberId,
    effectiveOwnerUserId,
    refresh,
    repairSpouseEdge,
  } = useFamilyGraph();

  // Allow ?debug=1 in staging to expose the panel even when isStaff is
  // false, so non-admin reviewers can reproduce the diagnostic.
  const [debugQuery, setDebugQuery] = useState(false);
  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const sp = new URLSearchParams(window.location.search);
      setDebugQuery(sp.get('debug') === '1');
    } catch {
      /* ignore */
    }
  }, []);

  // ─── HARD VISIBILITY GATE (production hosts NEVER show this UI) ────────
  // The panel only appears when BOTH conditions hold:
  //   1. running on a deploypad.app dev/staging host (DEV_BYPASS_TEST_MODE)
  //   2. the authenticated user is staff (owner/admin) — OR the URL has
  //      ?debug=1 (still scoped under #1 so production stays clean)
  //
  // Production hosts (kinscircleapp.com / www.kinscircleapp.com / any
  // non-deploypad domain) → DEV_BYPASS_TEST_MODE === false → enabled is
  // always false → the component renders nothing, no spacing
  // placeholders, no diagnostic overlays, no developer console UI.
  //
  // The panel remains diagnostic-only even on dev and staging.
  // Repair requests produce review evidence but execute no mutation.
  const enabled = DEV_BYPASS_TEST_MODE && (isStaff || debugQuery);

  const [expanded, setExpanded] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<RepairResult | null>(null);

  // Auto-pick Terrance Lewis Sr. by default — that's the row the
  // operator is currently chasing. Falls back to the first non-self row
  // if Terrance can't be found.
  useEffect(() => {
    if (!enabled) return;
    if (selectedId) return;
    if (members.length === 0) return;
    const byName = members.find((m) =>
      norm(m.name).includes('terrance') &&
      norm(m.name).includes('lewis'),
    );
    if (byName) {
      setSelectedId(byName.id);
      return;
    }
    const firstNonSelf = members.find(
      (m) => !(m.side === 'self' && m.relationship === 'unknown'),
    );
    if (firstNonSelf) setSelectedId(firstNonSelf.id);
  }, [enabled, members, selectedId]);

  const matches = useMemo(() => {
    const q = norm(search);
    if (!q) {
      return members
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 25);
    }
    return members
      .filter((m) => norm(m.name).includes(q))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 25);
  }, [members, search]);

  const selected = useMemo(
    () => members.find((m) => m.id === selectedId) ?? null,
    [members, selectedId],
  );

  // Resolve the same anchor the tree uses so every diagnostic below uses
  // the SAME viewerId the renderer uses. This is the canonical pattern
  // RelativeRoster + PendingApprovalsPanel already follow.
  const treeAnchor = useMemo(
    () => resolveViewerAnchor(members, viewerMemberId),
    [members, viewerMemberId],
  );
  const viewerId = treeAnchor?.id ?? null;

  const diagnosticBranchLayoutAuthority =
    useMemo(() => {
      const branch =
        resolveCanonicalTreeBranchConsumer({
          members,
          viewerId,
        });

      const layoutPath =
        resolveCanonicalTreeLayoutPathRuntime({
          members,
          viewerId,
        });

      return createTreeRuntimeBranchLayoutAuthoritySnapshot({
        viewerMemberId:
          viewerId,
        branch,
        layoutPath,
      });
    }, [members, viewerId]);

  // Live pyramid layout result for the SAME inputs the rendered tree
  // uses, so we can read tier / x / y / overflow status straight off
  // the layout output without speculating.
  const layout = useMemo(() => {
    try {
      return computePyramidLayout({
        structuralMembers: members,
        visibleMembers: members,
        branchLayoutAuthority:
          diagnosticBranchLayoutAuthority,
        centeredMemberId: null,
        youName: 'You',
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[GraphDebugPanel] layout compute failed', err);
      return null;
    }
  }, [
    members,
    diagnosticBranchLayoutAuthority,
  ]);

  const unplaced = useMemo(
    () => computeUnplacedIds(members, treeAnchor),
    [members, treeAnchor],
  );

  // Per-selection diagnostics. Computed eagerly from the current
  // members list so the panel reflects live state on every refresh.
  const diag = useMemo(() => {
    if (!selected) return null;

    const s = selected;
    // Spouse anchor — forward direction first (s.spouse_id → partner),
    // then reverse (any other row whose spouse_id points at s).
    let spouseAnchor: GraphMember | null = null;
    let spouseDirection: 'forward' | 'reverse' | 'none' = 'none';
    if (s.spouseId) {
      const partner = members.find((m) => m.id === s.spouseId);
      if (partner) {
        spouseAnchor = partner;
        spouseDirection = 'forward';
      }
    }
    if (!spouseAnchor) {
      const reverse = members.find(
        (m) =>
          m.id !== s.id &&
          m.spouseId === s.id &&
          m.approvalStatus !== 'rejected',
      );
      if (reverse) {
        spouseAnchor = reverse;
        spouseDirection = 'reverse';
      }
    }

    // Reciprocal? (BOTH sides agree).
    const reciprocal =
      !!spouseAnchor &&
      s.spouseId === spouseAnchor.id &&
      spouseAnchor.spouseId === s.id;

    // Synthetic spouse_partner_edges view — the table does not exist in
    // the current schema, but the operator's mental model still uses
    // it. We surface every row pair that effectively IS a spouse edge,
    // including the half-link case where only one side stored the link.
    const spousePartnerEdges: Array<{
      from: { id: string; name: string };
      to: { id: string; name: string };
      reciprocal: boolean;
    }> = [];
    const seenPair = new Set<string>();
    for (const m of members) {
      if (m.id !== s.id && m.spouseId !== s.id) continue;
      if (m.id === s.id) {
        const partner = members.find((p) => p.id === m.spouseId);
        if (!partner) continue;
        const key = [m.id, partner.id].sort().join('|');
        if (seenPair.has(key)) continue;
        seenPair.add(key);
        spousePartnerEdges.push({
          from: { id: m.id, name: m.name },
          to: { id: partner.id, name: partner.name },
          reciprocal: partner.spouseId === m.id,
        });
      } else {
        const key = [m.id, s.id].sort().join('|');
        if (seenPair.has(key)) continue;
        seenPair.add(key);
        spousePartnerEdges.push({
          from: { id: m.id, name: m.name },
          to: { id: s.id, name: s.name },
          reciprocal: s.spouseId === m.id,
        });
      }
    }

    // Visibility on the tree — true when the layout produced a card
    // for this member that is NOT in the overflow row.
    let layoutCard: { x: number; y: number; tier: number; overflow: boolean } | null = null;
    if (layout) {
      const card = layout.cards.find(
        (c) => c.member?.id === s.id || (c.isYou && s.id === viewerId),
      );
      if (card) {
        layoutCard = {
          x: card.x,
          y: card.y,
          tier: card.tier,
          overflow: !!card.needsConnection,
        };
      }
    }

    // Anchor visibility — the spouse anchor must itself be drawable
    // for the spouse-anchored tier inheritance to kick in.
    let anchorVisible: boolean | null = null;
    if (spouseAnchor && layout) {
      const card = layout.cards.find(
        (c) => c.member?.id === spouseAnchor!.id ||
              (c.isYou && spouseAnchor!.id === viewerId),
      );
      anchorVisible = !!card && !card.needsConnection;
    }

    // Placement proof.
    let proof: { passes: boolean; reason: string };
    try {
      const passes = isPlacementProvable(s, treeAnchor, members);
      let reason = '';
      if (!passes) {
        const k = viewerId ? computeKinship(members, viewerId, s.id) : null;
        if (!treeAnchor) reason = 'No viewer anchor resolved.';
        else if (s.spouseId && !members.some((m) => m.id === s.spouseId)) {
          reason = `spouse_id ${s.spouseId} points at a row that is not in the loaded graph.`;
        } else if (k && (k.code === 'extended' || k.code === 'unknown')) {
          reason = `Kinship engine returned '${k.code}' — no parent_ids path AND no provable spouse link.`;
        } else {
          reason = 'No path back to the viewer via parent_ids or spouse_id.';
        }
      } else {
        reason = 'Path proven.';
      }
      proof = { passes, reason };
    } catch (err: any) {
      proof = { passes: false, reason: err?.message || 'proof threw' };
    }

    // Duplicate detection — strict normalized name match within the
    // SAME effective owner. We display every duplicate we find so the
    // operator can see them BEFORE they hit the repair button.
    const duplicates = members.filter(
      (m) =>
        m.id !== s.id &&
        same(m.name, s.name) &&
        m.ownerUserId === s.ownerUserId,
    );

    // Counts of preserved data per row in the dup set (reassures the
    // operator that the merge will preserve photos, memories, comments).
    const dataCount = (rowId: string) => ({
      memories: memories.filter((mm) => mm.memberId === rowId).length,
      photos: photos.filter((pp) => pp.memberId === rowId).length,
      comments: comments.filter((cc) => cc.memberId === rowId).length,
      hasNotes: !!members.find((m) => m.id === rowId)?.notes?.trim(),
    });

    // Marion-specific son-in-law label, when applicable. We compute
    // kinship FROM Marion's perspective (rather than the viewer's) only
    // when Marion is identifiable, so the operator can see the correct
    // "Son-in-law to Marion" classification independently of who the
    // current logged-in viewer is.
    const marion = members.find(
      (m) => norm(m.name).startsWith('marion '),
    );
    let labelToMarion: string | null = null;
    if (marion) {
      try {
        labelToMarion = kinshipLabel(s, marion.id, members);
      } catch {
        labelToMarion = null;
      }
    }
    const labelToViewer = viewerId ? kinshipLabel(s, viewerId, members) : '(no viewer)';

    return {
      member: s,
      spouseAnchor,
      spouseDirection,
      reciprocal,
      spousePartnerEdges,
      layoutCard,
      anchorVisible,
      proof,
      duplicates,
      dataCount,
      labelToMarion,
      labelToViewer,
      isUnplaced: unplaced.has(s.id),
    };
  }, [
    selected,
    members,
    memories,
    photos,
    comments,
    layout,
    viewerId,
    treeAnchor,
    unplaced,
  ]);

  const handleRepair = async () => {
    if (!selected || !diag) {
      return;
    }

    if (busy) {
      return;
    }

    setBusy(true);

    const startedAt =
      new Date().toISOString();

    try {
      const anchor =
        diag.spouseAnchor ?? null;

      const candidates =
        [selected, ...diag.duplicates]
          .sort(
            (left, right) =>
              new Date(
                left.createdAt,
              ).getTime() -
              new Date(
                right.createdAt,
              ).getTime(),
          );

      const canonical =
        candidates[0] ?? selected;

      const duplicateIds =
        candidates
          .slice(1)
          .map(
            member =>
              member.id,
          );

      const currentLink:
        RepairResult['finalSpouseLink'] =
        anchor &&
        selected.spouseId === anchor.id &&
        anchor.spouseId === selected.id
          ? 'reciprocal'
          : anchor &&
              (
                selected.spouseId ===
                  anchor.id ||
                anchor.spouseId ===
                  selected.id
              )
            ? 'one-sided'
            : anchor
              ? 'missing'
              : 'unknown';

      const evidence = {
        generatedAt:
          new Date().toISOString(),
        source:
          'GraphDebugPanel',
        mode:
          'review-required',
        selectedMemberId:
          selected.id,
        canonicalMemberId:
          canonical.id,
        duplicateCandidateIds:
          duplicateIds,
        spouseAnchorId:
          anchor?.id ?? null,
        currentSpouseLink:
          currentLink,
        mutationExecuted:
          false,
        requiredAuthority:
          'transactional-governed-duplicate-and-spouse-repair',
      };

      if (
        typeof window !== 'undefined'
      ) {
        (
          window as unknown as
            Record<string, unknown>
        ).__kcGraphDebugRepairReview =
          evidence;

        window.dispatchEvent(
          new CustomEvent(
            'kc:graph-debug-repair-review',
            {
              detail:
                evidence,
            },
          ),
        );
      }

      setResult({
        startedAt,
        finishedAt:
          new Date().toISOString(),
        finalSpouseLink:
          currentLink,
        duplicatesMerged:
          0,
        duplicateNamesKept:
          canonical.name,
        spouseAnchorId:
          anchor?.id ?? null,
        spouseAnchorName:
          anchor?.name ?? null,
        steps: [
          {
            label:
              'Canonical candidate identified',
            ok:
              true,
            detail:
              `${canonical.name} (${canonical.id.slice(0, 8)}…)`,
          },
          {
            label:
              'Duplicate candidates reviewed',
            ok:
              duplicateIds.length === 0
                ? true
                : null,
            detail:
              duplicateIds.length === 0
                ? 'No duplicate candidates detected.'
                : `${duplicateIds.length} candidate(s) require transactional Governor review.`,
          },
          {
            label:
              'Current spouse link inspected',
            ok:
              currentLink ===
              'reciprocal',
            detail:
              currentLink,
          },
          {
            label:
              'Tree mutation executed',
            ok:
              true,
            detail:
              'No. Review evidence only.',
          },
        ],
      });

      toast.info(
        duplicateIds.length > 0 ||
        currentLink !== 'reciprocal'
          ? 'Repair evidence prepared. No Tree data was changed.'
          : 'No governed repair is currently required.',
      );
    } catch (error: any) {
      setResult({
        startedAt,
        finishedAt:
          new Date().toISOString(),
        finalSpouseLink:
          'unknown',
        duplicatesMerged:
          0,
        duplicateNamesKept:
          selected.name,
        spouseAnchorId:
          null,
        spouseAnchorName:
          null,
        steps: [
          {
            label:
              'Review generation',
            ok:
              false,
            detail:
              error?.message ||
              'Unknown review error.',
          },
        ],
      });

      toast.error(
        error?.message ||
          'Could not prepare repair review evidence.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (!enabled) return null;

  return (
    <div className="rounded-2xl bg-white border-2 border-slate-300 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-5 py-4 flex items-center gap-3 bg-gradient-to-r from-slate-100 to-slate-50 hover:bg-slate-100 transition"
      >
        <div className="w-10 h-10 rounded-full bg-slate-900 text-amber-300 flex items-center justify-center shadow-sm">
          <Bug className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <h3 className="font-mono text-sm font-bold text-slate-900 leading-tight">
            Graph Debug Panel
          </h3>
          <p className="text-[11px] text-slate-600">
            Dev / staging only — surfaces every signal that contributes to
            visible placement for one selected person.
          </p>
        </div>
        <span className="text-[10px] font-mono uppercase font-bold text-slate-700 bg-amber-200 rounded px-2 py-0.5 mr-2">
          {isStaff ? 'staff' : 'debug=1'}
        </span>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-slate-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-500" />
        )}
      </button>

      {expanded && (
        <div className="px-5 pb-5 pt-2 space-y-4">
          {/* Person picker */}
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-slate-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search any relative by name…"
                className="flex-1 text-xs font-mono bg-white border border-slate-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-slate-500"
              />
              <button
                type="button"
                onClick={() => {
                  refresh().catch(() => {});
                }}
                className="text-[11px] font-bold text-slate-700 hover:bg-slate-200 rounded-md px-2 py-1.5 inline-flex items-center gap-1 border border-slate-300"
                title="Force re-fetch from the database"
              >
                <RefreshCw className="w-3 h-3" />
                Refresh
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
              {matches.length === 0 && (
                <span className="text-[11px] text-slate-500 italic">No matches.</span>
              )}
              {matches.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setSelectedId(m.id)}
                  className={`text-[11px] font-mono rounded-full px-2.5 py-1 border transition ${
                    selectedId === m.id
                      ? 'bg-slate-900 text-amber-300 border-slate-900'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                  }`}
                >
                  {m.name}
                  {m.id === viewerId ? ' (YOU)' : ''}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-slate-500 font-mono">
              owner_user_id={effectiveOwnerUserId?.slice(0, 8) || 'null'}…  ·  viewerId=
              {viewerId?.slice(0, 8) || 'null'}…  ·  members.length={members.length}
            </div>
          </div>

          {/* Diagnostic table for the selected person */}
          {!selected && (
            <div className="text-xs text-slate-500 italic">
              Pick a relative above to see the live diagnostic table.
            </div>
          )}

          {selected && diag && (
            <div className="rounded-xl border-2 border-slate-300 bg-white overflow-hidden">
              <div className="px-3 py-2 bg-slate-900 text-amber-300 font-mono text-xs font-bold flex items-center justify-between">
                <span>Diagnostic — {selected.name}</span>
                <span className="opacity-70">{selected.id}</span>
              </div>
              <DiagnosticTable
                rows={[
                  { label: 'person id', value: <code>{selected.id}</code> },
                  { label: 'name', value: <code>{selected.name}</code> },
                  {
                    label: 'relationship label (stored)',
                    value: (
                      <code>
                        {selected.relationship}
                        {selected.relationshipOverride
                          ? ` · override: "${selected.relationshipOverride}"`
                          : ''}
                      </code>
                    ),
                  },
                  {
                    label: 'displayed label (to YOU)',
                    value: <code>{diag.labelToViewer}</code>,
                  },
                  {
                    label: 'displayed label (to Marion)',
                    value: (
                      <code>
                        {diag.labelToMarion ?? '(Marion not in graph)'}
                      </code>
                    ),
                    ok:
                      diag.labelToMarion === null
                        ? null
                        : diag.labelToMarion === 'Son-in-law' ||
                          diag.labelToMarion === 'Daughter-in-law' ||
                          /son.?in.?law/i.test(diag.labelToMarion),
                    hint:
                      diag.labelToMarion &&
                      !/son.?in.?law/i.test(diag.labelToMarion) &&
                      norm(selected.name).includes('terrance')
                        ? 'Expected "Son-in-law" — current label suggests the spouse_id ↔ Chekesha link is missing or one-sided.'
                        : null,
                  },
                  {
                    label: 'owner_user_id',
                    value: <code>{selected.ownerUserId}</code>,
                  },
                  {
                    label: 'parent_ids',
                    value: (
                      <code>
                        {(selected.parentIds ?? []).length === 0
                          ? '[]'
                          : `[${(selected.parentIds ?? [])
                              .map((p) => p.slice(0, 8) + '…')
                              .join(', ')}]`}
                      </code>
                    ),
                    hint:
                      (selected.parentIds ?? []).length === 0
                        ? 'No parents — for an in-law spouse this is EXPECTED. Spouse anchor proves placement.'
                        : null,
                  },
                  {
                    label: 'spouse_id (forward)',
                    value: <code>{selected.spouseId ?? 'null'}</code>,
                  },
                  {
                    label: 'spouse_partner_edges (synthetic view)',
                    value:
                      diag.spousePartnerEdges.length === 0 ? (
                        <code>none</code>
                      ) : (
                        <ul className="text-[11px] font-mono">
                          {diag.spousePartnerEdges.map((e, i) => (
                            <li key={i}>
                              {e.from.name} → {e.to.name}{' '}
                              <span
                                className={
                                  e.reciprocal ? 'text-emerald-600' : 'text-amber-600'
                                }
                              >
                                ({e.reciprocal ? 'reciprocal' : 'one-sided'})
                              </span>
                            </li>
                          ))}
                        </ul>
                      ),
                    ok:
                      diag.spousePartnerEdges.length === 0
                        ? null
                        : diag.spousePartnerEdges.every((e) => e.reciprocal),
                  },
                  {
                    label: 'spouse anchor id',
                    value: <code>{diag.spouseAnchor?.id ?? '(none)'}</code>,
                    ok: !!diag.spouseAnchor,
                  },
                  {
                    label: 'spouse anchor name',
                    value: <code>{diag.spouseAnchor?.name ?? '(none)'}</code>,
                  },
                  {
                    label: 'spouse link direction',
                    value: <code>{diag.spouseDirection}</code>,
                    ok: diag.reciprocal,
                    hint: diag.spouseAnchor && !diag.reciprocal
                      ? 'Half-link detected — only one side carries spouse_id. Tap "Repair spouse anchor" to reciprocate.'
                      : null,
                  },
                  {
                    label: 'anchor visible on tree',
                    value:
                      diag.anchorVisible === null
                        ? <code>(no anchor)</code>
                        : <code>{diag.anchorVisible ? 'yes' : 'no'}</code>,
                    ok: diag.anchorVisible,
                  },
                  {
                    label: 'placement proof passes',
                    value: <code>{diag.proof.passes ? 'yes' : 'no'}</code>,
                    ok: diag.proof.passes,
                    hint: diag.proof.passes ? null : diag.proof.reason,
                  },
                  {
                    label: 'computeUnplacedIds marks unplaced',
                    value: <code>{diag.isUnplaced ? 'yes (Needs Connection)' : 'no'}</code>,
                    ok: !diag.isUnplaced,
                  },
                  {
                    label: 'pyramidLayout assigned tier',
                    value:
                      diag.layoutCard
                        ? <code>{diag.layoutCard.tier}</code>
                        : <code>(none — not in cards[])</code>,
                    ok: diag.layoutCard ? !diag.layoutCard.overflow : false,
                  },
                  {
                    label: 'final card x / y',
                    value:
                      diag.layoutCard
                        ? <code>x={Math.round(diag.layoutCard.x)} · y={Math.round(diag.layoutCard.y)}</code>
                        : <code>(unplaced)</code>,
                  },
                  {
                    label: 'in overflow / Needs Connection row',
                    value: (
                      <code>
                        {diag.layoutCard?.overflow
                          ? 'yes — bottom overflow row'
                          : diag.isUnplaced
                          ? 'yes (set membership)'
                          : 'no'}
                      </code>
                    ),
                    ok: !(diag.layoutCard?.overflow || diag.isUnplaced),
                  },
                  {
                    label: 'cached/persisted layout overriding live placement',
                    value: <code>no — live recompute on every render</code>,
                    ok: true,
                    hint: 'KinsCircle does not persist a pyramid layout cache. Visible placement is derived live from the graph on every render.',
                  },
                  {
                    label: 'duplicate records (same normalized name + same owner)',
                    value:
                      diag.duplicates.length === 0 ? (
                        <code>none — single record</code>
                      ) : (
                        <ul className="text-[11px] font-mono">
                          {[selected, ...diag.duplicates].map((d) => {
                            const c = diag.dataCount(d.id);
                            return (
                              <li key={d.id}>
                                {d.id.slice(0, 8)}… · created {d.createdAt.slice(0, 10)} ·
                                {' '}memories={c.memories} photos={c.photos} comments={c.comments}
                                {c.hasNotes ? ' · has notes' : ''}
                              </li>
                            );
                          })}
                        </ul>
                      ),
                    ok: diag.duplicates.length === 0,
                  },
                ]}
              />
            </div>
          )}

          {/* Repair button + result */}
          {selected && (
            <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleRepair}
                  disabled={busy}
                  className="text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 disabled:opacity-50 rounded-full px-4 py-2 inline-flex items-center gap-1.5 shadow-sm"
                >
                  <Wrench className="w-3.5 h-3.5" />
                  {busy ? 'Preparing review…' : 'Prepare repair review'}
                </button>
                <span className="text-[11px] text-amber-900">
                  Reviews duplicate candidates and the spouse_id state for{' '}
                  <code className="font-mono">
                    {diag?.spouseAnchor?.name ??
                      (norm(selected.name).includes('terrance')
                        ? 'Chekesha Graham-Lewis'
                        : 'the resolved anchor')}
                  </code>
                  , then emits governed repair evidence without changing data.
                </span>
              </div>
              {result && (
                <RepairResultBlock result={result} />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Sub-components ─────────────────────────────────────────────────────
const DiagnosticTable: React.FC<{ rows: DiagRow[] }> = ({ rows }) => (
  <table className="w-full text-[12px] font-mono">
    <tbody>
      {rows.map((r, i) => (
        <tr
          key={r.label}
          className={
            i % 2 === 0
              ? 'bg-white border-t border-slate-100'
              : 'bg-slate-50/50 border-t border-slate-100'
          }
        >
          <td className="align-top px-3 py-2 w-[280px] text-slate-700">
            {truthIcon(r.ok ?? null)}
            {r.label}
          </td>
          <td className="align-top px-3 py-2 text-slate-900 break-words">
            {r.value}
            {r.hint && (
              <div className="mt-1 text-[10.5px] text-amber-700 italic">{r.hint}</div>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const RepairResultBlock: React.FC<{ result: RepairResult }> = ({ result }) => (
  <div className="rounded-lg bg-white border border-amber-200 p-2.5 font-mono text-[11px] space-y-1">
    <div className="flex items-center justify-between text-amber-900 font-bold">
      <span>Repair review</span>
      <span className="text-[10px] opacity-70">
        {result.startedAt.slice(11, 19)} → {result.finishedAt?.slice(11, 19) ?? '…'}
      </span>
    </div>
    {result.spouseAnchorName && (
      <div className="text-slate-700">
        anchor: <code>{result.spouseAnchorName}</code>{' '}
        ({result.spouseAnchorId?.slice(0, 8)}…)
      </div>
    )}
    <div className="text-slate-700">
      mutations executed: <code>{result.duplicatesMerged}</code> · current spouse link:{' '}
      <code
        className={
          result.finalSpouseLink === 'reciprocal'
            ? 'text-emerald-700'
            : result.finalSpouseLink === 'missing'
            ? 'text-rose-700'
            : 'text-amber-700'
        }
      >
        {result.finalSpouseLink}
      </code>
    </div>
    <ul className="space-y-0.5 mt-1">
      {result.steps.map((s, i) => (
        <li key={i} className="flex items-start gap-1.5">
          {truthIcon(s.ok ?? null)}
          <span className="flex-1">
            {s.label}
            {s.detail && (
              <span className="text-slate-500"> — {s.detail}</span>
            )}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

export default GraphDebugPanel;
